import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import store from 'store';
import { addToCartEventData, buildItemTrackingData } from 'lib/tracking';
import datalayer from 'mixins/datalayer';
import { i18n } from 'lib/i18n.js';
import bus from 'lib/bus';
import productBundle from 'mixins/product-bundle';
import productSelector from 'mixins/product-selector';

import {
  decode,
  cleanseProductJson,
  getBreakpoint,
  formatPrice,
  getStarsHTML,
  decodeIDvariant,
  htlmDecode,
} from 'utils/general';

const isDesktopLayout = () => {
  return ['md', 'l'].includes(getBreakpoint());
};

export default (el) =>
  new Vue({
    el,
    store,
    i18n,
    name: 'Product Feature',
    components: {
      'product-notify-me-form': () =>
        import('../product-notify-me-form/product-notify-me-form.vue'),
      'product-selector': () =>
        import('../product-selector/product-selector.vue'),
      'quantity-selector': () =>
        import('../quantity-selector/quantity-selector.vue'),
      'product-selector-group': () =>
        import('../product-selector-group/product-selector-group.vue'),
    },
    mixins: [datalayer, productBundle, productSelector],
    data() {
      const props = { ...el.dataset };
      const initialProduct =
        props?.initialProduct &&
        cleanseProductJson(JSON.parse(decode(props.initialProduct)));
      return {
        ...props,
        initialProduct,
        quantity: 1,
        showPrice: true,
        OGOptIn: false,
        isDesktopLayout: isDesktopLayout(),
        enableAddToCart: true,
      };
    },
    async mounted() {
      if (Boolean(this.initialProduct)) {
        this.updateProduct(this.initialProduct.handle);
        if (this.initialProduct.bundledProducts.length) {
          this.isBundle = true;
          await this.fetchBundleProductsByHandles(
            this.initialProduct.bundledProducts,
          );
        }
      }
    },
    computed: {
      ...mapState('product', [
        'loading',
        'product',
        'variant',
        'sizes',
        'metafields',
      ]),
      variantAvailable() {
        return this.variant.available;
      },
      stars() {
        if (!this.product?.reviewsAverage) return '';
        return getStarsHTML(this.product.reviewsAverage);
      },
      reviewsCount() {
        return this.product?.reviewsCount || 0;
      },
      reviewsText() {
        if (!this.reviewsCount) return '';
        return `(${this.reviewsCount})`;
      },
      starsAriaLabel() {
        if (!this.product?.reviewsAverage) return '';
        return `${this.product.reviewsAverage} out of 5 stars`;
      },
      readReviewsLink() {
        if (!this.reviewsCount) return '';
        return `View ${this.reviewsCount} Review${this.reviewsCount > 1 ? 's' : ''}`;
      },
      rating() {
        if (!this.product?.reviewsAverage) return '';
        return this.product.reviewsAverage;
      },
      variantTitle() {
        return this.getVariantTitle(this.product.title);
      },
      variantDiscountPrice() {
        if (
          !this.variant.price ||
          (!this.variant.compareAtPrice && !this.OGOptIn)
        )
          return false;
        return formatPrice(this.variant.price)
          .replace('.00', '')
          .replace(',00', '');
      },
      resizeHandler() {
        this.isDesktopLayout = isDesktopLayout();
      },
      variantPrice() {
        if (!this.variant.price) return '';

        let price = this.variant.price;
        if (this.variant.compareAtPrice) {
          price = this.variant.compareAtPrice;
        }
        return formatPrice(price).replace('.00', '').replace(',00', '');
      },
      variantIDClean() {
        const res = this.variant.id
          ? this.variant.id.includes('==')
            ? decodeIDvariant(this.variant.id)
            : this.variant.id
          : false;

        if (res) {
          this.configureSubscription();
        }
        return res;
      },
      showFindShadeButton() {
        if (!this.metafields) {
          return false;
        }
        return ![true, 'true'].includes(
          this.metafields?.general?.hide_shade_buttons,
        );
      },
      showTryShadeOnButton() {
        if (!this.metafields || window.__GLOBAL__.disableVto) {
          return false;
        }
        return ![true, 'true'].includes(
          this.metafields?.general?.hide_try_shade_on_button,
        );
      },
      addText() {
        return this.gwp === true ? 'Add' : this.$t('products.card.add');
      },
    },
    methods: {
      ...mapActions('cart', [
        'addBundleItemsToCart',
        'addToCart',
        'openCart',
        'closeCart',
        'updateChangePending',
      ]),
      ...mapActions('global'),
      ...mapActions('product', ['updateProduct']),
      getVariantTitle(t) {
        return t.split(window.__GLOBAL__.productDelimiter)[0];
      },
      configureSubscription() {
        const OGIntervalMaxRetry = 10;
        let OGIntervalCurrentRetry = 0;
        const OGInterval = setInterval(() => {
          if (window.OG) {
            clearInterval(OGInterval);
            this.OGOptIn = false;
            window.OGOptIn = false;
            if (
              OG.getOptins().find(
                (optIn) => +optIn.product === +this.variantIDClean,
              )
            ) {
              this.OGOptIn = true;
              window.OGOptIn = true;
            }
            OG.addOptinChangedCallback((e) => {
              this.OGOptIn = e.optedIn;
              window.OGOptIn = e.optedIn;
            });
          }
          OGIntervalCurrentRetry++ > OGIntervalMaxRetry &&
            clearInterval(OGInterval);
        }, 300);
      },
      decodeHtml(html) {
        var txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
      },
      updateBundle({ product, key }) {
        this.bundleProductsData[parseInt(key)] = product;
        !product.available
          ? (this.unavailableVariant = true)
          : (this.unavailableVariant = false);
        product.title = this.decodeHtml(product.title);
      },
      addProduct() {
        const properties = {
          _product_collection: this.product.categories
            ? this.product.categories[0]
            : null,
          _product_tags: this.product.tags,
          _product_rating:
            this.product.reviewsAverage ||
            this.product.metafields?.yotpo?.reviews_average,
          _origin: document.body.dataset.origin,
          _origin_product: document.body.dataset.originProduct,
          _index: 0,
        };

        if (this.isBundle) {
          this.addBundleToCart({
            price: this.variant.price,
            title: this.variantTitle,
            productObject: this.product,
            quantity: this.quantity,
            properties,
          });
        } else {
          this.addToCart({
            variant: this.variant,
            quantity: this.quantity,
            properties,
            moduleName: 'product feature'
          });
        }
        const extraData = this.getExtraData();
        this.dataLayerPush(
          'addToCart',
          addToCartEventData(this.product, this.quantity, extraData),
        );
      },
      openReviews() {
        bus.$emit('open-reviews-accordion');
      },
      updateQuantity(quantity) {
        const prevQuantity = this.quantity;
        this.quantity = quantity;

        if (prevQuantity < quantity) {
          const extraData = this.getExtraData();
          this.dataLayerPush(
            'addToCart',
            addToCartEventData(this.product, quantity, extraData),
          );
        }
      },
      getExtraData() {
        const isWinLocation = (str) =>
          window.location.pathname.includes('/' + str);
        let addToCartType = '';
        if (isWinLocation('collections')) {
          addToCartType = isWinLocation('products')
            ? 'Product Detail Page'
            : 'Product List Page';
        } else if (isWinLocation('cart')) {
          addToCartType = 'Cart';
        } else if (isWinLocation('products')) {
          addToCartType = 'Product Detail Page';
        }
        return {
          addToCartType,
          sku: this.product.sku,
        };
      },
    },
  });
